// Menüpunkte "Zusammenführen" und "Löschen" im Lead-Menü "…" und ihre Fenster (Runde 7, Agent 7C). `LeadKopf.tsx` (Agent 7B) ruft nur
// diesen Hook auf, hängt `punkte` ins Menü und zeigt `dialoge` irgendwo im Kopf; was darin steht, ändert 7C allein.
//
//   const dubletten = useDublettenMenue({ leadId, leadName });
//   … punkte: [..., ...dubletten.punkte]      … JSX: {dubletten.dialoge}
//
// Der Hook lädt beim Öffnen des Leads einmal die möglichen Dubletten (`dublettenApi.zuLead`) und zeigt ihre Zahl im Menü. Nach dem
// Zusammenführen oder Löschen sendet der Server `lead.merged` bzw. `lead.deleted`; hier geht es zum bleibenden Lead bzw. zur Liste.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { dublettenApi, type DubletteLead } from "../../api/dubletten";
import { datenschutzApi } from "../../api/datenschutz";
import { useEchtzeit } from "../../api/echtzeit";
import { useAuth } from "../../auth/AuthContext";
import { StatusPille } from "../Bausteine";
import { Dialog } from "../Dialog";
import { useMeldung } from "../Meldungen";
import type { MenuePunkt } from "../Menue";
import { LeadWahl } from "../../dubletten/LeadWahl";
import { LoeschenDialog } from "../../dubletten/LoeschenDialog";
import { tagText, ZusammenfuehrenDialog, type LeadRef } from "../../dubletten/ZusammenfuehrenDialog";
import { ART_TITEL, TX } from "../../dubletten/texte";
import "../../dubletten/dubletten.css";

export interface DublettenMenueEingabe {
  leadId: string;
  leadName: string;
}

export interface DublettenMenue {
  punkte: MenuePunkt[];
  dialoge: ReactNode;
}

interface Paar {
  ziel: LeadRef;
  quelle: LeadRef;
}

export function useDublettenMenue({ leadId, leadName }: DublettenMenueEingabe): DublettenMenue {
  const navigate = useNavigate();
  const meldung = useMeldung();
  const { nutzer } = useAuth();
  const recht = (r: string) => !!nutzer?.permissions.includes(r);
  const darfMerge = recht("leads_zusammenfuehren");
  const darfLoeschen = recht("datenschutz_verwalten");
  const darfAuskunft = recht("datenschutz_verwalten");

  const [hinweise, setHinweise] = useState<DubletteLead[]>([]);
  const [wahlOffen, setWahlOffen] = useState(false);
  const [listeOffen, setListeOffen] = useState(false);
  const [loeschenOffen, setLoeschenOffen] = useState(false);
  const [paar, setPaar] = useState<Paar | null>(null);

  const dieser: LeadRef = { id: leadId, name: leadName };

  const hinweiseLaden = useCallback(() => {
    dublettenApi
      .zuLead(leadId)
      .then((r) => setHinweise(r.data))
      .catch(() => setHinweise([]));
  }, [leadId]);
  useEffect(() => {
    setHinweise([]);
    hinweiseLaden();
  }, [hinweiseLaden]);
  useEchtzeit(["lead.merged"], hinweiseLaden);

  const zusammengefuehrt = async (ziel: string, protokollId: number | undefined) => {
    setPaar(null);
    setWahlOffen(false);
    setListeOffen(false);
    meldung.zeige(
      "ok",
      `${TX.zfFertig} ${TX.zfFertigClose}`,
      protokollId
        ? {
            label: TX.rueckgaengig,
            onClick: () =>
              void dublettenApi
                .rueckgaengig(protokollId)
                .then(() => {
                  meldung.ok("Zusammenführung rückgängig gemacht.");
                  hinweiseLaden();
                })
                .catch((e) => meldung.fehler(e, "Nicht rückgängig gemacht")),
          }
        : undefined,
      12000,
    );
    if (ziel !== leadId) navigate(`/lead/${encodeURIComponent(ziel)}`);
    else hinweiseLaden();
  };

  const punkte: MenuePunkt[] = [
    { label: TX.menueUeberschrift, ueberschrift: true, trenner: true },
    ...(hinweise.length
      ? [{ label: TX.menueDubletten(hinweise.length), icon: "zusammen", onClick: () => setListeOffen(true) } as MenuePunkt]
      : []),
    {
      label: TX.menueZusammenfuehren,
      icon: "zusammen",
      deaktiviert: !darfMerge,
      hinweis: darfMerge ? undefined : "Dafür fehlt das Recht „Leads zusammenführen“.",
      onClick: () => setWahlOffen(true),
    },
    ...(darfAuskunft
      ? [{ label: TX.menueAuskunft, icon: "karte", onClick: () => window.open(datenschutzApi.auskunftUrl(leadId), "_self") } as MenuePunkt]
      : []),
    {
      label: TX.menueLoeschen,
      icon: "papierkorb",
      gefahr: true,
      deaktiviert: !darfLoeschen,
      hinweis: darfLoeschen ? undefined : "Dafür fehlt das Recht „Leads löschen“.",
      onClick: () => setLoeschenOffen(true),
    },
  ];

  const dialoge = (
    <>
      <Dialog titel={TX.zfLeadWaehlen} offen={wahlOffen} onSchliessen={() => setWahlOffen(false)} breite={480}>
        <p className="leise">
          „{leadName}“ wird mit einem anderen Lead zusammengeführt. Im nächsten Schritt wählst du, welcher von beiden bestehen bleibt.
        </p>
        <LeadWahl
          autoFokus
          ausschliessen={[leadId]}
          platzhalter={TX.zfLeadSuche}
          onWahl={(l) => {
            setWahlOffen(false);
            setPaar({ ziel: dieser, quelle: { id: l.leadId, name: l.name } });
          }}
        />
      </Dialog>

      <Dialog titel={TX.alsHinweis} offen={listeOffen} onSchliessen={() => setListeOffen(false)} breite={620}>
        {hinweise.length ? (
          <ul className="dub-hinweise">
            {hinweise.map((h) => {
              const andere: LeadRef = { id: h.id, name: h.name ?? h.id };
              return (
                <li key={h.id} className="dub-hinweise__zeile">
                  <div className="dub-hinweise__text">
                    <a className="dub-hinweise__name" href={`/lead/${encodeURIComponent(h.id)}`} onClick={(e) => { e.preventDefault(); setListeOffen(false); navigate(`/lead/${encodeURIComponent(h.id)}`); }}>
                      {h.name}
                    </a>
                    <span className="dub-hinweise__meta">
                      {h.status_label ? <StatusPille label={h.status_label} klein schlicht /> : null}
                      <span className="leise">angelegt {tagText(h.date_created) || "–"}</span>
                    </span>
                    <span className="leise">
                      {(h.gruende ?? []).map((g) => `${ART_TITEL[g.art]}: ${g.wert}`).join(" · ")}
                    </span>
                  </div>
                  <div className="dub-hinweise__knoepfe">
                    <button type="button" className="knopf knopf--klein" disabled={!darfMerge} onClick={() => setPaar({ ziel: dieser, quelle: andere })}>
                      {TX.zusammenfuehren}
                    </button>
                    <button
                      type="button"
                      className="knopf knopf--klein knopf--leise"
                      title={TX.keineDubletteTitel}
                      disabled={!darfMerge}
                      onClick={() =>
                        void dublettenApi
                          .ignorieren(leadId, h.id)
                          .then(() => {
                            setHinweise((l) => l.filter((x) => x.id !== h.id));
                            meldung.ok(TX.gemerkt + ".");
                          })
                          .catch((e) => meldung.fehler(e, "Nicht gemerkt"))
                      }
                    >
                      {TX.keineDublette}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="leise">{TX.keineHinweise}</p>
        )}
      </Dialog>

      {paar ? (
        <ZusammenfuehrenDialog
          offen
          ziel={paar.ziel}
          quelle={paar.quelle}
          onSchliessen={() => setPaar(null)}
          onFertig={(e) => void zusammengefuehrt(e.ziel_lead_id, e.protokoll_id)}
        />
      ) : null}

      <LoeschenDialog
        offen={loeschenOffen}
        leadId={leadId}
        leadName={leadName}
        onSchliessen={() => setLoeschenOffen(false)}
        onFertig={() => {
          setLoeschenOffen(false); // die Meldung "Dieser Lead wurde gelöscht." kommt von der Lead-Seite (lead.deleted)
          navigate("/leads");
        }}
      />
    </>
  );

  return { punkte, dialoge };
}
